import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { ArrowRight, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * 4 axes du radar :
 * - Identité (2 champs : prenom, nom)
 * - Contact (2 champs : email, telephone)
 * - Entreprise (8 champs : entreprise, siret, statut_juridique, code_naf, date_creation_entreprise, effectif, chiffre_affaires, experience_professionnelle_annees)
 * - Adresse & Territoire (5 champs : adresse, code_postal, ville, pays, territoire)
 */
const SECTION_FIELDS = {
  Identité: ['prenom', 'nom'],
  Contact: ['email', 'telephone'],
  Entreprise: ['entreprise', 'siret', 'statut_juridique', 'code_naf', 'date_creation_entreprise', 'effectif', 'chiffre_affaires', 'experience_professionnelle_annees'],
  Adresse: ['adresse', 'code_postal', 'ville', 'pays', 'territoire'],
};

const FIELD_LABELS = {
  prenom: 'Prénom', nom: 'Nom',
  email: 'Email', telephone: 'Téléphone',
  entreprise: 'Raison sociale', siret: 'SIRET', statut_juridique: 'Statut juridique',
  code_naf: 'Code APE / NAF', date_creation_entreprise: 'Date de création', effectif: 'Effectif', chiffre_affaires: 'Chiffre d\'affaires',
  experience_professionnelle_annees: 'Expérience professionnelle',
  adresse: 'Adresse', code_postal: 'Code postal', ville: 'Ville', pays: 'Pays', territoire: 'Territoire',
};

const isFilled = (val) =>
  val !== undefined && val !== null && String(val).trim() !== '' && String(val).trim() !== '0';

/**
 * Composant Radar de complétude du profil membre.
 * @param {Object} props
 * @param {Object} props.membre — l'objet membre avec ses champs
 * @param {Function} [props.onCompleteClick] — callback déclenché au clic "Compléter le profil"
 * @param {boolean} [props.compact=false] — version compacte pour intégration dans la modale
 */
export default function ProfileCompletenessRadar({ membre, onCompleteClick, compact = false }) {
  const stats = useMemo(() => {
    const sectionScores = {};
    const missing = [];
    let totalFilled = 0;
    let totalFields = 0;

    Object.entries(SECTION_FIELDS).forEach(([section, fields]) => {
      let filled = 0;
      fields.forEach((field) => {
        totalFields += 1;
        if (isFilled(membre?.[field])) {
          filled += 1;
          totalFilled += 1;
        } else {
          missing.push({ section, field, label: FIELD_LABELS[field] || field });
        }
      });
      sectionScores[section] = { filled, total: fields.length, ratio: filled / fields.length };
    });

    return {
      sectionScores,
      missing,
      totalFilled,
      totalFields,
      percentage: Math.round((totalFilled / totalFields) * 100),
    };
  }, [membre]);

  // Calcul des points pour le radar SVG (4 axes, 0° en haut, sens horaire)
  const radarPoints = useMemo(() => {
    const cx = 80; // centre x
    const cy = 80; // centre y
    const maxR = 60; // rayon max
    const sections = Object.keys(SECTION_FIELDS);
    const angles = sections.map((_, i) => -Math.PI / 2 + (i * 2 * Math.PI) / sections.length);
    const points = sections.map((section, i) => {
      const r = stats.sectionScores[section].ratio * maxR;
      return {
        x: cx + r * Math.cos(angles[i]),
        y: cy + r * Math.sin(angles[i]),
        section,
        ratio: stats.sectionScores[section].ratio,
        labelX: cx + (maxR + 14) * Math.cos(angles[i]),
        labelY: cy + (maxR + 14) * Math.sin(angles[i]),
        axisX: cx + maxR * Math.cos(angles[i]),
        axisY: cy + maxR * Math.sin(angles[i]),
      };
    });
    return { cx, cy, maxR, sections, points };
  }, [stats]);

  const polygonPath = radarPoints.points.map((p) => `${p.x},${p.y}`).join(' ');

  // Couleur dynamique selon le score
  const scoreColor =
    stats.percentage >= 90 ? { text: 'text-emerald-600', bg: 'bg-emerald-50', stroke: '#10b981', border: 'border-emerald-200' }
    : stats.percentage >= 70 ? { text: 'text-blue-600', bg: 'bg-blue-50', stroke: '#3b82f6', border: 'border-blue-200' }
    : stats.percentage >= 50 ? { text: 'text-amber-600', bg: 'bg-amber-50', stroke: '#f59e0b', border: 'border-amber-200' }
    : { text: 'text-red-600', bg: 'bg-red-50', stroke: '#ef4444', border: 'border-red-200' };

  return (
    <div className={cn(
      'rounded-xl border bg-gradient-to-br from-slate-50 to-white overflow-hidden',
      scoreColor.border,
    )} data-testid="profile-completeness-radar">
      <div className="px-4 py-2.5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-purple-600" />
        <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
          Score de complétude du profil
        </span>
      </div>

      <div className={cn('p-4 grid gap-4', compact ? 'grid-cols-1 sm:grid-cols-[180px_1fr]' : 'grid-cols-[180px_1fr]')}>
        {/* Radar SVG */}
        <div className="flex items-center justify-center relative">
          <svg viewBox="0 0 160 160" className="w-40 h-40" data-testid="radar-svg">
            <defs>
              <radialGradient id="radar-fill" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={scoreColor.stroke} stopOpacity="0.45" />
                <stop offset="100%" stopColor={scoreColor.stroke} stopOpacity="0.08" />
              </radialGradient>
            </defs>
            {/* Cercles concentriques */}
            {[0.25, 0.5, 0.75, 1].map((r, i) => (
              <circle
                key={i}
                cx={radarPoints.cx}
                cy={radarPoints.cy}
                r={radarPoints.maxR * r}
                fill="none"
                stroke="#e2e8f0"
                strokeWidth="1"
                strokeDasharray={i === 3 ? '0' : '2 3'}
              />
            ))}
            {/* Axes */}
            {radarPoints.points.map((p, i) => (
              <line
                key={i}
                x1={radarPoints.cx}
                y1={radarPoints.cy}
                x2={p.axisX}
                y2={p.axisY}
                stroke="#cbd5e1"
                strokeWidth="0.8"
              />
            ))}
            {/* Polygone score */}
            <polygon
              points={polygonPath}
              fill="url(#radar-fill)"
              stroke={scoreColor.stroke}
              strokeWidth="2"
              strokeLinejoin="round"
              className="transition-all duration-700"
            />
            {/* Points */}
            {radarPoints.points.map((p, i) => (
              <circle
                key={i}
                cx={p.x}
                cy={p.y}
                r="3"
                fill={scoreColor.stroke}
                stroke="white"
                strokeWidth="1.5"
              />
            ))}
            {/* Labels axes */}
            {radarPoints.points.map((p, i) => (
              <text
                key={i}
                x={p.labelX}
                y={p.labelY}
                fontSize="9"
                fontWeight="600"
                fill="#64748b"
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {p.section}
              </text>
            ))}
          </svg>
          {/* Centre : pourcentage flottant */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none mt-1">
            <div className="text-center">
              <div className={cn('text-2xl font-bold leading-none', scoreColor.text)} data-testid="completeness-percentage">
                {stats.percentage}<span className="text-sm">%</span>
              </div>
              <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide mt-0.5">
                {stats.totalFilled} / {stats.totalFields}
              </div>
            </div>
          </div>
        </div>

        {/* Détails par section + champs manquants */}
        <div className="flex flex-col gap-2 min-w-0">
          {/* Barres de progression par section */}
          <div className="space-y-1.5">
            {Object.entries(stats.sectionScores).map(([section, sc]) => (
              <div key={section} className="flex items-center gap-2">
                <span className="text-[11px] font-semibold text-slate-600 w-20 flex-shrink-0">{section}</span>
                <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={cn('h-full rounded-full transition-all duration-700', {
                      'bg-emerald-500': sc.ratio >= 0.9,
                      'bg-blue-500': sc.ratio >= 0.7 && sc.ratio < 0.9,
                      'bg-amber-500': sc.ratio >= 0.5 && sc.ratio < 0.7,
                      'bg-red-500': sc.ratio < 0.5,
                    })}
                    style={{ width: `${sc.ratio * 100}%` }}
                  />
                </div>
                <span className="text-[10px] font-mono font-semibold text-slate-500 w-8 text-right flex-shrink-0">
                  {sc.filled}/{sc.total}
                </span>
              </div>
            ))}
          </div>

          {/* Liste champs manquants */}
          {stats.missing.length > 0 ? (
            <div className="mt-1 pt-2 border-t border-slate-100">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 text-amber-500" />
                {stats.missing.length} champ(s) manquant(s)
              </p>
              <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                {stats.missing.slice(0, 8).map((m) => (
                  <span
                    key={m.field}
                    className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium"
                    title={`Section : ${m.section}`}
                  >
                    {m.label}
                  </span>
                ))}
                {stats.missing.length > 8 && (
                  <span className="text-[10px] text-slate-500 font-mono">+{stats.missing.length - 8} …</span>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-1 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-emerald-600">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span className="text-[11px] font-semibold">Profil complet — toutes les informations sont renseignées</span>
            </div>
          )}

          {/* Action */}
          {stats.missing.length > 0 && onCompleteClick && (
            <Button
              size="sm"
              onClick={onCompleteClick}
              className={cn(
                'h-8 mt-1 gap-1.5 self-start text-xs font-semibold shadow-sm',
                stats.percentage >= 70 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-500 hover:bg-amber-600',
              )}
              data-testid="complete-profile-btn"
            >
              Compléter le profil
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
